'use client';

import { MessageMarkdown } from '@/app/(protected)/search/message-markdown';
import { type ChatRefDoc } from '@/app/services/chat';
import { llmChat, type ReferenceItem } from '@/app/services/llm';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { publishHydratedReference } from '@/hooks/use-hydrated-references';
import {
  getOriginalLanguageLabel,
  isEffectivelyChineseOriginal,
  shouldAutoTranslateAbstract,
} from '@/lib/abstract-language';
import { getReferenceDocId } from '@/lib/reference-doc-id';
import { getOpenAlexId } from '@/lib/reference-identifiers';
import { convertAbstractToMarkdownAdvanced } from '@/lib/util';
import copy from 'copy-to-clipboard';
import { Copy } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export type AbstractLocale = 'zh' | 'en';

function formatDate(doc: ChatRefDoc): string {
  const y = doc.pub_year ?? '';
  const m = doc.pub_month ? `-${doc.pub_month}` : '';
  const d = doc.pub_day ? `-${doc.pub_day}` : '';
  return `${y}${m}${d}`;
}

function formatAuthors(doc: ChatRefDoc): string {
  if (!doc.authors || doc.authors.length === 0) return '';
  const names = doc.authors.map((author) => {
    const directName = author.name?.trim();
    if (directName) return directName;
    const foreName = author.fore_name?.trim();
    const lastName = author.last_name?.trim();
    return [foreName, lastName].filter(Boolean).join(' ');
  });
  return names.filter(Boolean).join(', ');
}

function formatAffiliations(doc: ChatRefDoc): string[] {
  if (!doc.authors) return [];
  return Array.from(
    new Set(
      doc.authors.flatMap((author) =>
        (author.affiliations ?? []).map((affiliation) => affiliation.name),
      ),
    ),
  ).filter(Boolean);
}

function formatSearchGateway(doc: ChatRefDoc): string {
  const source = getReferenceSource(doc);
  if (source === 'pubmed') return 'PubMed';
  if (source === 'openalex') return 'OpenAlex';
  return doc.search_gateway?.trim() || 'PubMed';
}

function getReferenceSource(doc: ChatRefDoc): 'pubmed' | 'openalex' {
  const normalized = doc.search_gateway?.trim().toLowerCase();
  if (normalized === 'pubmed') return 'pubmed';
  if (normalized === 'openalex' || getOpenAlexId(doc)) return 'openalex';
  return 'pubmed';
}

function SourceBadge({ value }: { value: string }) {
  return (
    <span className="ml-2 inline-flex translate-y-[-2px] items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium leading-none text-primary">
      {value}
    </span>
  );
}

function handleCopy(text: string, message: string) {
  copy(text);
  toast.success(message);
}

function AuthorsLine({
  authors,
  expanded,
  onToggle,
}: {
  authors: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const canToggle = authors.length > 160;

  return (
    <div className="text-sm leading-6 text-foreground/85">
      <div className={expanded ? undefined : 'line-clamp-2'}>{authors}</div>
      {canToggle && (
        <Button
          type="button"
          variant="link"
          className="mt-1 h-auto p-0 text-xs text-muted-foreground"
          onClick={onToggle}
        >
          {expanded ? '收起作者' : '展开全部作者'}
        </Button>
      )}
    </div>
  );
}

type Props = {
  current: ChatRefDoc;
  locale: AbstractLocale;
  fulltextActions?: ReactNode;
  deferAutoTranslate?: boolean;
};

export function AbstractPreviewContent({
  current,
  locale,
  fulltextActions,
  deferAutoTranslate = false,
}: Props) {
  const zhTranslation =
    current.translation?.['zh-cn'] ?? current.translation?.zh;
  const initialZhTitle = zhTranslation?.title ?? '';
  const initialZhAbstract = zhTranslation?.abstract ?? '';
  const [streamedZh, setStreamedZh] = useState({
    docId: null as string | null,
    title: '',
    abstract: '',
  });
  const [translateStatus, setTranslateStatus] = useState({
    docId: null as string | null,
    value: 'idle' as 'idle' | 'loading' | 'done' | 'error',
  });
  const [expandedAuthorsDocId, setExpandedAuthorsDocId] = useState<
    string | null
  >(null);
  const ctrlRef = useRef<AbortController | null>(null);
  const requestedTranslationsRef = useRef(new Set<string>());
  const enTitle = current.title ?? '';
  const enAbstract = current.abstract ?? '';
  const isChineseOriginal = isEffectivelyChineseOriginal(current);
  const originalLanguageLabel = getOriginalLanguageLabel(
    current.pub_language,
  );
  const originalTitle = current.title ?? '';
  const originalAbstract = current.abstract ?? '';
  const docId = getReferenceDocId(current) || null;
  const streamedTitle = streamedZh.docId === docId ? streamedZh.title : '';
  const streamedAbstract =
    streamedZh.docId === docId ? streamedZh.abstract : '';
  const status =
    translateStatus.docId === docId ? translateStatus.value : 'idle';
  const hasInitialZh = Boolean(initialZhTitle || initialZhAbstract);
  const zhTitle =
    streamedTitle || initialZhTitle || (isChineseOriginal ? originalTitle : '');
  const zhAbstract =
    streamedAbstract ||
    initialZhAbstract ||
    (isChineseOriginal ? originalAbstract : '');
  const hasZh = zhTitle || zhAbstract;

  useEffect(() => {
    if (
      !docId ||
      !shouldAutoTranslateAbstract({
        locale,
        hasInitialZh,
        docId,
        deferAutoTranslate,
        pubLanguage: current.pub_language,
        isChineseOriginal,
      })
    ) {
      return;
    }

    const activeDocId = docId;
    const requestKey = `${activeDocId}:${locale}`;
    if (requestedTranslationsRef.current.has(requestKey)) {
      return;
    }
    requestedTranslationsRef.current.add(requestKey);

    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    void llmChat<{ result?: ReferenceItem; type: string }>({
      path: `/v2/doc/${encodeURIComponent(activeDocId)}/translate_doc_stream`,
      body: {},
      ctrl,
      onOpen: () => {
        setTranslateStatus({ docId: activeDocId, value: 'loading' });
      },
      onMessage: (message) => {
        const result = message.result;
        if (!result) return;
        const doneTranslation =
          result.translation?.['zh-cn'] ?? result.translation?.zh;
        if (doneTranslation) {
          setStreamedZh({
            docId: activeDocId,
            title: doneTranslation.title ?? '',
            abstract: doneTranslation.abstract ?? '',
          });
          publishHydratedReference({
            doc_id: activeDocId,
            translation: {
              'zh-cn': {
                title: doneTranslation.title ?? '',
                abstract: doneTranslation.abstract ?? '',
              },
            },
          });
          setTranslateStatus({ docId, value: 'done' });
          return;
        }
        setStreamedZh((prev) => {
          const sameDoc = prev.docId === activeDocId;
          return {
            docId: activeDocId,
            title: result.title
              ? (sameDoc ? prev.title : '') + result.title
              : sameDoc
                ? prev.title
                : '',
            abstract: result.abstract
              ? (sameDoc ? prev.abstract : '') + result.abstract
              : sameDoc
                ? prev.abstract
                : '',
          };
        });
      },
      onClose: () => {
        if (ctrlRef.current === ctrl) {
          ctrlRef.current = null;
        }
        setTranslateStatus((currentStatus) =>
          currentStatus.docId === activeDocId &&
          currentStatus.value === 'loading'
            ? { docId: activeDocId, value: 'done' }
            : currentStatus,
        );
      },
      onError: () => {
        if (ctrlRef.current === ctrl) {
          ctrlRef.current = null;
        }
        if (!ctrl.signal.aborted) {
          requestedTranslationsRef.current.delete(requestKey);
          setTranslateStatus({ docId: activeDocId, value: 'error' });
        }
      },
    });

    return () => ctrl.abort();
  }, [
    deferAutoTranslate,
    docId,
    hasInitialZh,
    initialZhAbstract,
    initialZhTitle,
    current.pub_language,
    isChineseOriginal,
    locale,
  ]);
  const authors = formatAuthors(current);
  const authorsExpanded = expandedAuthorsDocId === docId;
  const toggleAuthors = () => {
    setExpandedAuthorsDocId((currentDocId) =>
      currentDocId === docId ? null : docId,
    );
  };
  const affiliations = formatAffiliations(current);
  const publicationLine = [
    current.publication ?? current.publisher ?? '',
    formatDate(current),
  ]
    .filter(Boolean)
    .join(' · ');
  const searchGateway = formatSearchGateway(current);
  const source = getReferenceSource(current);
  const openAlexId = getOpenAlexId(current);
  const showPmid = source === 'pubmed' && Boolean(current.pmid);
  const showOpenAlexId = source === 'openalex' && Boolean(openAlexId);
  const hasFulltextActions = Boolean(fulltextActions);
  const hasMeta =
    authors ||
    publicationLine ||
    showPmid ||
    showOpenAlexId ||
    current.doi ||
    hasFulltextActions;

  const citationText = `# ${originalTitle}\n${originalAbstract}\n>${
    current.publication ?? current.publisher ?? ''
  }    ${formatDate(current)}`;

  return (
    <Tabs value={locale}>
      <TabsContent value="en">
        <div className="pb-4">
          <h2 className="border-b pb-2 text-lg font-semibold">
            {enTitle}
            {searchGateway && <SourceBadge value={searchGateway} />}
          </h2>
          {hasMeta && (
            <div className="space-y-2 border-b border-border/60 py-3">
              {authors && (
                <AuthorsLine
                  authors={authors}
                  expanded={authorsExpanded}
                  onToggle={toggleAuthors}
                />
              )}
              {publicationLine && (
                <div className="text-sm text-muted-foreground">
                  {publicationLine}
                </div>
              )}
              {(showPmid || showOpenAlexId || current.doi) && (
                <div className="flex flex-wrap gap-2">
                  {showPmid && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-full px-3 text-xs shadow-none"
                      onClick={() =>
                        handleCopy(current.pmid ?? '', 'PMID 已复制')
                      }
                    >
                      PMID: {current.pmid}
                    </Button>
                  )}
                  {showOpenAlexId && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-full px-3 text-xs shadow-none"
                      onClick={() =>
                        handleCopy(openAlexId, 'OpenAlex ID 已复制')
                      }
                    >
                      OpenAlex ID: {openAlexId}
                    </Button>
                  )}
                  {current.doi && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-full px-3 text-xs shadow-none"
                      onClick={() =>
                        handleCopy(current.doi ?? '', 'DOI 已复制')
                      }
                    >
                      DOI: {current.doi}
                    </Button>
                  )}
                </div>
              )}
              {affiliations.length > 0 && (
                <div className="text-xs leading-5 text-muted-foreground">
                  {affiliations.slice(0, 3).join('；')}
                  {affiliations.length > 3 ? '…' : ''}
                </div>
              )}
              {fulltextActions && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {fulltextActions}
                </div>
              )}
            </div>
          )}
          <div>
            <h3 className="py-2 font-semibold">{originalLanguageLabel}摘要</h3>
            <MessageMarkdown
              content={convertAbstractToMarkdownAdvanced(enAbstract)}
              refs={[]}
              onDetail={() => {}}
            />
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold">
              {current.publication ?? current.publisher}.
            </span>
            <span className="ml-2 italic">{formatDate(current)}.</span>
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-full"
            onClick={() => handleCopy(citationText, '复制成功')}
          >
            <Copy size={18} />
          </Button>
        </div>
      </TabsContent>
      <TabsContent value="zh">
        {hasZh ? (
          <div className="pb-4">
            <h2 className="border-b pb-2 text-lg font-semibold">
              {zhTitle || enTitle}
              {searchGateway && <SourceBadge value={searchGateway} />}
            </h2>
            {hasMeta && (
              <div className="space-y-2 border-b border-border/60 py-3">
                {authors && (
                  <AuthorsLine
                    authors={authors}
                    expanded={authorsExpanded}
                    onToggle={toggleAuthors}
                  />
                )}
                {publicationLine && (
                  <div className="text-sm text-muted-foreground">
                    {publicationLine}
                  </div>
                )}
                {(showPmid || showOpenAlexId || current.doi) && (
                  <div className="flex flex-wrap gap-2">
                    {showPmid && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full px-3 text-xs shadow-none"
                        onClick={() =>
                          handleCopy(current.pmid ?? '', 'PMID 已复制')
                        }
                      >
                        PMID: {current.pmid}
                      </Button>
                    )}
                    {showOpenAlexId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full px-3 text-xs shadow-none"
                        onClick={() =>
                          handleCopy(openAlexId, 'OpenAlex ID 已复制')
                        }
                      >
                        OpenAlex ID: {openAlexId}
                      </Button>
                    )}
                    {current.doi && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full px-3 text-xs shadow-none"
                        onClick={() =>
                          handleCopy(current.doi ?? '', 'DOI 已复制')
                        }
                      >
                        DOI: {current.doi}
                      </Button>
                    )}
                  </div>
                )}
                {affiliations.length > 0 && (
                  <div className="text-xs leading-5 text-muted-foreground">
                    {affiliations.slice(0, 3).join('；')}
                    {affiliations.length > 3 ? '…' : ''}
                  </div>
                )}
                {fulltextActions && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {fulltextActions}
                  </div>
                )}
              </div>
            )}
            <div>
              <h3 className="py-2 font-semibold">摘要</h3>
              <MessageMarkdown
                content={convertAbstractToMarkdownAdvanced(
                  zhAbstract || enAbstract,
                )}
                refs={[]}
                onDetail={() => {}}
              />
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {deferAutoTranslate
              ? '正在加载中文翻译…'
              : status === 'error'
                ? '中文翻译失败，请稍后重试'
                : '正在生成中文翻译…'}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
